import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import * as isoCountries from 'i18n-iso-countries';
import { Country } from '@spartacus/core';

import { AddressDataEligibility, AddressSuggestion, EmailDataEligibility, TelDataEligibility } from '../models/dqe.model';
import { ConfigurationService } from './configuration.service';
import { BaseStoreService } from './base-store.service';

@Injectable({
  providedIn: 'root'
})
export class DqeService {
  private forwardedForIp$ = new BehaviorSubject<string>('127.0.0.1');

  constructor(
    private readonly http: HttpClient,
    private readonly baseStoreService: BaseStoreService,
    private readonly configService: ConfigurationService,
    @Inject(PLATFORM_ID) readonly platformId: any
  ) {
    if (isPlatformBrowser(platformId)) {
      this.http.post<string>('/get/ip', {}).subscribe((ip) => {
        this.forwardedForIp$.next(ip.split(',')[0]);
      });
    }
  }

  get country(): Country {
    return this.baseStoreService.baseStore.deliveryCountries[0];
  }

  suggest(input: string, _type?: string): Observable<string[]> {
    const { baseUrl, subscriptionKey } = this.configService.config.dqe;

    return this.forwardedForIp$.pipe(
      switchMap((ip) => this.http.post<string[]>(`${baseUrl}/emailSuggest`, {
        'Email': input,
        'Pays': isoCountries.alpha2ToAlpha3(this.country?.isocode)
      }, {
        headers: this.requestHeaders(subscriptionKey, ip)
      }))
    );
  }

  validate(input: string, _type?: string): Observable<EmailDataEligibility> {
    const { baseUrl, subscriptionKey } = this.configService.config.dqe;

    return this.forwardedForIp$.pipe(
      switchMap((ip) => this.http.post<EmailDataEligibility>(`${baseUrl}/emailScore`, {
        'Email': input
      }, {
        headers: this.requestHeaders(subscriptionKey, ip)
      }))
    );
  }

  validateTel(input: string, _type?: string): Observable<TelDataEligibility> {
    const { baseUrl, subscriptionKey } = this.configService.config.dqe;

    return this.forwardedForIp$.pipe(
      switchMap((ip) => this.http.post<TelDataEligibility>(`${baseUrl}/mobileScore`, {
        'Tel': input.trim() !== '' ? input : null,
        'Pays': isoCountries.alpha2ToAlpha3(this.country?.isocode)
      }, {
        headers: this.requestHeaders(subscriptionKey, ip)
      }))
    );
  }

  suggestAddress(input: string): Observable<AddressSuggestion[]> {
    const { baseUrl, subscriptionKey } = this.configService.config.dqe;

    return this.forwardedForIp$.pipe(
      switchMap((ip) => this.http.post<AddressSuggestion[]>(`${baseUrl}/addressSuggest`, {
        'Adresse': input,
        'Pays': isoCountries.alpha2ToAlpha3(this.country?.isocode)
      }, {
        headers: this.requestHeaders(subscriptionKey, ip)
      }))
    );
  }

  validateAddress(address?: string, postalCode?: string, town?: string, complement = '', placeName = ''): Observable<AddressDataEligibility> {
    const { baseUrl, subscriptionKey } = this.configService.config.dqe;
    const MODIFICATION_MODE = 'O';

    return this.forwardedForIp$.pipe(
      switchMap((ip) => this.http.post<AddressDataEligibility>(`${baseUrl}/addressValidation`, {
        'Adresse': `${complement}|${address}|${placeName}|${postalCode}|${town}`,
        'Pays': isoCountries.alpha2ToAlpha3(this.country?.isocode),
        'Modification': MODIFICATION_MODE
      }, {
        headers: this.requestHeaders(subscriptionKey, ip)
      }))
    );
  }

  private requestHeaders(key: string, ip: string): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'Ocp-Apim-Subscription-Key': key,
      'X-Forwarded-For': ip
    });
  }
}
